<script>
import { defineComponent } from "vue";
import { VInput } from "vuetify/components";
import ValidationMixin from "@/mixins/validation.mixin";

const humanFormat = "MM/DD/YYYY",
  withoutPaddingZero = "M/D/YYYY",
  dbFormat = "YYYY-MM-DD";

export default defineComponent({
  name: "DatePicker",
  extends: VInput,
  mixins: [ValidationMixin],
  inheritAttrs: false,
  props: {
    id: String,
    label: String,
    modelValue: String,
    max: {
      type: String,
      default: "",
    },
    min: {
      type: String,
      default: "",
    },
    required: Boolean,
    disabled: Boolean,
    skipValidation: Boolean,
    containerClass: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue", "search"],
  data() {
    return {
      show: false,
      dateMask: {
        mask: "##/##/####",
        eager: true,
      },
      formattedDate: null,
      hasError: false,
      errorMessage: "",
    };
  },
  computed: {
    date: {
      get() {
        const date = new Date(this.modelValue);
        const userTimezoneOffset = date.getTimezoneOffset() * 60000;
        const offsetDate = new Date(date.getTime() + userTimezoneOffset);
        return this.modelValue ? offsetDate : null;
      },
      set(value) {
        this.formattedDate = this.stringify(value);
        this.$emit("update:modelValue", this.$dayjs(value).format(dbFormat));
      },
    },
    preparedRules() {
      const rules = [];

      if (this.required) {
        rules.push(this.requiredValidator);
      }
      rules.push(this.dateFormatValidator, this.maxDateValidator, this.minDateValidator);

      return rules;
    },
  },
  watch: {
    formattedDate(value) {
      if (!value) {
        this.clearDate();
      }
      if (this.dateInputValidator(value) === true && this.dateFormatValidator(value) === true) {
        this.$emit("update:modelValue", this.toDate(value).format(dbFormat));
      }
    },
    modelValue(value) {
      if (!value) {
        this.formattedDate = null;
        return;
      }
      this.formattedDate = this.stringify(value);
    },
    date() {
      this.show = false;
    },
    show: {
      handler(value) {
        if (!value) {
          this.handleError();
        }
      },
      immediate: false,
    },
  },
  mounted() {
    if (this.modelValue) {
      this.formattedDate = this.stringify(this.modelValue);
    }
  },
  methods: {
    search() {
      this.$emit("search", this.$dayjs(this.modelValue).format(dbFormat));
    },
    toDate(value) {
      return this.$dayjs(value, [dbFormat, humanFormat, withoutPaddingZero], true);
    },
    stringify(date) {
      return this.$dayjs(date).format(humanFormat);
    },
    dateFormatValidator(value) {
      if (!value) return true;
      return this.toDate(value).isValid() || "Wrong date format";
    },
    maxDateValidator(value) {
      if (!this.max || !value || this.skipValidation) return true;
      return (
        this.toDate(value).isBefore(this.$dayjs(this.max)) ||
        "Date can not be after " + this.$dayjs(this.max).format(humanFormat)
      );
    },
    minDateValidator(value) {
      if (!this.min || !value || this.skipValidation) return true;
      return (
        this.toDate(value).isAfter(this.$dayjs(this.min)) ||
        "Date can not be before " + this.$dayjs(this.min).format(humanFormat)
      );
    },
    clearDate() {
      this.$emit("update:modelValue", null);
    },
    handleError() {
      this.errorMessage = "";

      this.hasError = this.preparedRules.some((rule) => {
        const validationResult = rule(this.formattedDate);

        if (typeof validationResult === "string" && !this.errorMessage) {
          this.errorMessage = validationResult;
        }

        return validationResult !== true;
      });
    },
  },
});
</script>

<template>
  <v-menu
    ref="menu"
    v-model="show"
    :close-on-content-click="false"
    min-width="auto"
    offset="10"
    transition="scale-transition"
  >
    <template #activator="{ props }">
      <div :class="containerClass" class="d-flex">
        <v-text-field
          :id="id"
          v-model="formattedDate"
          v-maska:[dateMask]
          :class="{ required: required }"
          :clearable="true"
          :disabled="disabled"
          :error="hasError"
          :error-messages="errorMessage"
          :label="label"
          :rules="preparedRules"
          append-inner-icon="mdi-calendar-blank-outline"
          aria-autocomplete="none"
          autocomplete="off"
          color="primary"
          density="compact"
          hide-details="auto"
          placeholder="MM/DD/YYYY"
          v-bind="{ ...$attrs, ...props }"
          validate-on="submit"
          variant="outlined"
          @update:model-value="handleError"
          @click:clear="clearDate"
          @keyup.enter="search"
        />
      </div>
    </template>
    <vue-date-picker
      v-model="date"
      :enable-time-picker="false"
      :max-date="max"
      :min-date="min"
      auto-apply
      inline
      no-title
    />
  </v-menu>
</template>
